import { motion, type HTMLMotionProps } from "framer-motion";
import { tokens } from "@axisnode/design-tokens";
import type { ReactNode } from "react";

const ms = (value: string): number => Number.parseInt(value, 10);

/** Shared durations (ms) from design-tokens — never hard-coded elsewhere. */
export const motionDurations = {
  fast: ms(tokens.motion.duration.fast),
  base: ms(tokens.motion.duration.base),
  slow: ms(tokens.motion.duration.slow),
} as const;

/** Page enter/exit — APR-PP-001 §9 / OE-PP-001 M-3 Framer Motion primitive. */
export const pageTransition = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: {
    duration: motionDurations.base / 1000,
    ease: "easeOut" as const,
  },
};

export function PageTransition({
  children,
  ...rest
}: { children: ReactNode } & HTMLMotionProps<"div">) {
  return (
    <motion.div
      initial={pageTransition.initial}
      animate={pageTransition.animate}
      exit={pageTransition.exit}
      transition={pageTransition.transition}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/** List-item enter/exit — APR-PP-001 §9 primitive. */
export const listItemMotion = {
  initial: { opacity: 0, x: -6 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 6 },
  transition: {
    duration: motionDurations.fast / 1000,
    ease: "easeOut" as const,
  },
};

export function ListItemMotion({
  children,
  ...rest
}: { children: ReactNode } & HTMLMotionProps<"li">) {
  return (
    <motion.li
      initial={listItemMotion.initial}
      animate={listItemMotion.animate}
      exit={listItemMotion.exit}
      transition={listItemMotion.transition}
      {...rest}
    >
      {children}
    </motion.li>
  );
}

/** Off-canvas drawer presence variants — used by Drawer (AnimatePresence). */
export const drawerPresence = {
  backdrop: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: motionDurations.fast / 1000 },
  },
  panel: {
    initial: { x: "-100%" },
    animate: { x: 0 },
    exit: { x: "-100%" },
    transition: {
      duration: motionDurations.base / 1000,
      ease: "easeOut" as const,
    },
  },
};

/** Bottom-sheet presence variants — used by Sheet (AnimatePresence). Same
 * shape as drawerPresence (slide + backdrop fade) but from the bottom edge —
 * the tap-reachable direction on mobile, where Sheet is meant to live. */
export const sheetPresence = {
  backdrop: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: motionDurations.fast / 1000 },
  },
  panel: {
    initial: { y: "100%" },
    animate: { y: 0 },
    exit: { y: "100%" },
    transition: {
      duration: motionDurations.base / 1000,
      ease: "easeOut" as const,
    },
  },
};

/** Modal presence variants — used by Modal (AnimatePresence). */
export const modalPresence = {
  backdrop: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: motionDurations.fast / 1000 },
  },
  panel: {
    initial: { opacity: 0, scale: 0.96, y: 8 },
    animate: { opacity: 1, scale: 1, y: 0 },
    exit: { opacity: 0, scale: 0.98, y: 4 },
    transition: {
      duration: motionDurations.base / 1000,
      ease: "easeOut" as const,
    },
  },
};
